import { useState } from "react";
import { ApiError, apiGet } from "../api/client";
import { useAppData } from "../state/AppData";
import {
  INSPECT_DESC,
  INSPECT_MODES,
  INSPECT_TITLE,
  INSPECT_WINDOWS,
  NO_EXAMPLE_TEXT,
  NO_ROUTES_TEXT,
  PAIR_HELP,
  PENDING_TEXT,
  PREFIX_HELP,
  buildQuery,
  errorText,
  hopStatus,
  hopStatusShort,
  isPending,
  mapNote,
  needsChoice,
  nodeLabel,
  packetsText,
  parsePrefixPath,
  receptionsText,
  roleTag,
  routesButtonText,
  routesShownText,
  relayNote,
  roleText,
  routeHopLabel,
  routeLine,
  seenText,
  summaryText,
  truncatedText,
  type Candidate,
  type Drawable,
  type ExampleResponse,
  type InspectGroup,
  type InspectMode,
  type InspectNode,
  type InspectPending,
  type InspectResponse,
  type InspectRoute,
  type InspectWindow,
  type PairEnd,
  type RouteHop,
} from "../lib/pathInspector";
import { HoverTip } from "./HoverTip";
import { PathInspectorMap } from "./PathInspectorMap";
import { SectionCard } from "./SectionCard";

// Analysis > MeshCore > Path Inspector. Everything the page says is decided by lib/pathInspector.ts; this file lays it out.
// It sits beside Paths because it answers the same kind of question (which routes did packets take), for one path or one
// pair of nodes you ask about, on request instead of from the stored report.

function nodeTip(n: InspectNode): string {
  const parts = [roleText(n.role), n.last_heard ? `last heard ${seenText(n.last_heard)}` : "never heard", n.has_position ? "has a position" : "no position"];
  return parts.join(", ");
}

function NodeLink({ node, children }: { node: { id: string }; children: string }) {
  return (
    <a href={`#/nodes/${encodeURIComponent(node.id)}`} className="node-name-link" onClick={(e) => e.stopPropagation()}>
      {children}
    </a>
  );
}

function Chip({ c }: { c: Candidate }) {
  return (
    <HoverTip text={`${nodeTip(c)}${c.remaining ? "" : ". Ruled out by the rest of the path"}`} className="paths-unclear-tip">
      <span className={`pi-chip${c.remaining ? "" : " pi-chip-out"}`}>
        <NodeLink node={c}>{nodeLabel(c)}</NodeLink>
        <span className="pi-chip-meta">{roleText(c.role)}{c.has_position ? "" : ", no position"}</span>
      </span>
    </HoverTip>
  );
}

function HopRows({ r }: { r: InspectResponse }) {
  return (
    <div className="node-list-table-wrap">
      <table className="node-list-table pi-hops">
        <thead>
          <tr>
            <th className="rf-num">#</th>
            <th>Prefix</th>
            <th>State</th>
            <th>Nodes this could be</th>
          </tr>
        </thead>
        <tbody>
          {r.hops.map((h, i) => (
            <tr key={i}>
              <td className="rf-num">{i + 1}</td>
              <td className="pi-hex">{h.hex}</td>
              <td>
                <span className={`pi-state pi-state-${hopStatusShort(h.kind)}`}>{hopStatus(h)}</span>
              </td>
              <td>
                {h.candidates.length === 0 ? (
                  <span className="node-detail-note">None</span>
                ) : (
                  <div className="pi-chips">
                    {h.candidates.map((c) => (
                      <Chip key={c.id} c={c} />
                    ))}
                    {h.n_candidates > h.candidates.length && (
                      <span className="node-detail-note">and {h.n_candidates - h.candidates.length} more</span>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HopView({ h }: { h: RouteHop }) {
  const label = routeHopLabel(h);
  const cls = `pi-hop${h.matched ? " pi-hop-matched" : ""}${h.role ? ` pi-hop-${h.role}` : ""}`;
  if (h.node_id !== null) {
    return (
      <span className={cls}>
        <NodeLink node={{ id: h.node_id }}>{label}</NodeLink>
        {roleTag(h.role) && <span className="path-tag">{roleTag(h.role)}</span>}
        {h.kind === "observer" && <span className="path-tag">Heard by</span>}
        {h.kind === "path" && <span className="path-tag">Resolved By Path</span>}
      </span>
    );
  }
  const tip =
    h.kind === "ambiguous"
      ? `Several nodes start with ${h.hex}; ${h.n} are still possible on this route. None is picked.`
      : h.kind === "impossible"
        ? `No consistent answer for ${h.hex} on this route. Left unresolved.`
        : `No node we know starts with ${h.hex}.`;
  return (
    <HoverTip text={tip} className="paths-unclear-tip">
      <span className={`${cls} pi-hop-unclear`}>{label}</span>
    </HoverTip>
  );
}

function RouteChain({ r }: { r: Drawable }) {
  return (
    <div className="pi-chain" aria-label={routeLine(r)}>
      {r.hops.map((h, i) => (
        <span key={i} className="pi-step">
          {i > 0 && <span className="pi-arrow" aria-hidden="true">→</span>}
          <HopView h={h} />
        </span>
      ))}
    </div>
  );
}

type Shown = { kind: "group" | "route"; group: number; route?: number };

function Counts({ x }: { x: InspectRoute | InspectGroup }) {
  return (
    <>
      <strong>{packetsText(x.packets)}</strong>
      <span>{receptionsText(x.receptions, x.n_observers)}</span>
      <span>Last seen {seenText(x.last_seen)}</span>
    </>
  );
}

function MapDetail({ x, theme }: { x: InspectRoute | InspectGroup; theme: "light" | "dark" }) {
  return (
    <div className="pi-route-detail">
      <PathInspectorMap key={`${theme}-${routeLine(x)}`} route={x} theme={theme} height={280} />
      <p className="node-detail-note">{mapNote(x)}</p>
      {x.observers.length > 0 && (
        <p className="node-detail-note">
          Heard by {x.observers.map((o) => `${o.name ?? "a station"} (${o.n})`).join(", ")}
          {x.n_observers > x.observers.length ? ` and ${x.n_observers - x.observers.length} more` : ""}. First seen {seenText(x.first_seen)}.
        </p>
      )}
    </div>
  );
}

// A group is the stretch that was asked about; under it, the full routes it came from. "Show on map" draws the
// stretch (on the group) or the whole route (on a route); "Show N routes" lists them.
function GroupList({ groups, theme, title }: { groups: InspectGroup[]; theme: "light" | "dark"; title?: string }) {
  const [map, setMap] = useState<Shown | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const mapIs = (group: number, route?: number) => map !== null && map.group === group && map.route === route;
  const toggleMap = (group: number, route?: number) =>
    setMap(mapIs(group, route) ? null : { kind: route === undefined ? "group" : "route", group, route });
  return (
    <div className="pi-routes">
      {title && <h4 className="rf-section-h">{title}</h4>}
      {groups.map((g, gi) => {
        const isOpen = open === gi;
        const note = routesShownText(g);
        return (
          <div key={gi} className={`pi-route pi-group${isOpen || mapIs(gi) ? " pi-route-open" : ""}`}>
            <div className="pi-route-row">
              <RouteChain r={g} />
              <div className="pi-route-count">
                <Counts x={g} />
                <span className="pi-btns">
                  <button type="button" className="sign-out-btn pi-map-btn" aria-expanded={mapIs(gi)} onClick={() => toggleMap(gi)}>
                    {mapIs(gi) ? "Hide map" : "Show on map"}
                  </button>
                  <button type="button" className="sign-out-btn pi-map-btn" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : gi)}>
                    {routesButtonText(g, isOpen)}
                  </button>
                </span>
              </div>
            </div>
            {mapIs(gi) && <MapDetail x={g} theme={theme} />}
            {isOpen && (
              <div className="pi-sub">
                {note && <p className="node-detail-note">{note}</p>}
                {g.routes.map((r, ri) => (
                  <div key={ri} className="pi-subroute">
                    <div className="pi-route-row">
                      <RouteChain r={r} />
                      <div className="pi-route-count">
                        <Counts x={r} />
                        <button type="button" className="sign-out-btn pi-map-btn" aria-expanded={mapIs(gi, ri)} onClick={() => toggleMap(gi, ri)}>
                          {mapIs(gi, ri) ? "Hide map" : "Show on map"}
                        </button>
                      </div>
                    </div>
                    {mapIs(gi, ri) && <MapDetail x={r} theme={theme} />}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function EndChoice({ label, end, onPick }: { label: string; end: PairEnd; onPick: (n: InspectNode) => void }) {
  if (end.node !== null) return null;
  return (
    <div className="pi-choice">
      <div className="k decode-heading">{label}: {end.matches.length} nodes match "{end.query}". Pick one.</div>
      <div className="pi-chips">
        {end.matches.map((n) => (
          <button key={n.id} type="button" className="pi-chip pi-chip-btn" onClick={() => onPick(n)} title={nodeTip(n)}>
            {nodeLabel(n)}
            <span className="pi-chip-meta">{roleText(n.role)} {n.id.slice(0, 6)}</span>
          </button>
        ))}
        {end.more && <span className="node-detail-note">More match than are shown. Type more of the name or id.</span>}
      </div>
    </div>
  );
}

export function PathInspectorTab() {
  const { effectiveTheme } = useAppData();
  const [mode, setMode] = useState<InspectMode>("prefix");
  const [win, setWin] = useState<InspectWindow>("7d");
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  // a node picked from a list of matches: the box shows its name, the search uses its id (a name can be shared)
  const [pickA, setPickA] = useState<InspectNode | null>(null);
  const [pickB, setPickB] = useState<InspectNode | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InspectResponse | null>(null);
  const [pending, setPending] = useState(false);

  async function example() {
    setError(null);
    setPending(false);
    setBusy(true);
    try {
      const ex = await apiGet<ExampleResponse | InspectPending>("/api/paths/inspect/example");
      if (isPending(ex)) {
        setPending(true);
      } else if (ex.path === null) {
        setError(NO_EXAMPLE_TEXT);
      } else {
        setA(ex.path);
        setBusy(false);
        await run("prefix", win, ex.path, "");
      }
    } catch (e) {
      setError(e instanceof ApiError ? errorText(e.status, e.message) : errorText(undefined, ""));
    } finally {
      setBusy(false);
    }
  }

  async function run(m: InspectMode, w: InspectWindow, x: string, y: string) {
    setError(null);
    setPending(false);
    if (m === "prefix" && x.trim() !== "") {
      const parsed = parsePrefixPath(x);
      if (parsed.error !== undefined) {
        setResult(null);
        setError(parsed.error);
        return;
      }
    }
    const url = buildQuery(m, w, x, y);
    if (url === null) {
      setResult(null);
      setError(m === "prefix" ? "Enter a path of hop prefixes, for example 4b,a2,7c." : "Enter both nodes: a node id prefix or part of a name.");
      return;
    }
    setBusy(true);
    try {
      const r = await apiGet<InspectResponse | InspectPending>(url);
      if (isPending(r)) {
        setResult(null);
        setPending(true);
      } else {
        setResult(r);
      }
    } catch (e) {
      setResult(null);
      setError(e instanceof ApiError ? errorText(e.status, e.message) : errorText(undefined, ""));
    } finally {
      setBusy(false);
    }
  }

  function pick(which: "a" | "b", n: InspectNode) {
    if (which === "a") {
      setPickA(n);
      setA(nodeLabel(n));
    } else {
      setPickB(n);
      setB(nodeLabel(n));
    }
    void run("pair", win, which === "a" ? n.id : pickA?.id ?? a, which === "b" ? n.id : pickB?.id ?? b);
  }

  const groups = result?.groups ?? [];
  const resolved = groups.filter((g) => g.match !== "possible");
  const possible = groups.filter((g) => g.match === "possible");
  const pairNote = result && result.mode === "pair" && !needsChoice(result) ? [relayNote(result.source?.node ?? null), relayNote(result.destination?.node ?? null)].filter(Boolean) : [];

  return (
    <div className="talkers-tab pi-tab">
      <SectionCard title={INSPECT_TITLE} description={INSPECT_DESC}>
        <div className="node-list-toolbar">
          <div className="theme-toggle" role="group" aria-label="What to look up">
            {INSPECT_MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                data-active={mode === m.id}
                onClick={() => {
                  setMode(m.id);
                  setResult(null);
                  setError(null);
                  setPending(false);
                }}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="theme-toggle" role="group" aria-label="Window">
            {INSPECT_WINDOWS.map((w) => (
              <button
                key={w}
                type="button"
                data-active={win === w}
                onClick={() => {
                  setWin(w);
                  if (result) void run(mode, w, pickA?.id ?? a, pickB?.id ?? b);
                }}
              >
                {w}
              </button>
            ))}
          </div>
        </div>

        <p className="node-detail-note">{mode === "prefix" ? PREFIX_HELP : PAIR_HELP}</p>
        <form
          className="pi-form"
          onSubmit={(e) => {
            e.preventDefault();
            void run(mode, win, pickA?.id ?? a, pickB?.id ?? b);
          }}
        >
          <input
            className="pi-input"
            aria-label={mode === "prefix" ? "Hop prefixes" : "Source node"}
            placeholder={mode === "prefix" ? "4b,a2,7c" : "Source: node id prefix or name"}
            spellCheck={false}
            value={a}
            onChange={(e) => {
              setA(e.target.value);
              setPickA(null);
            }}
          />
          {mode === "pair" && (
            <input
              className="pi-input"
              aria-label="Destination node"
              placeholder="Destination: node id prefix or name"
              spellCheck={false}
              value={b}
              onChange={(e) => {
                setB(e.target.value);
                setPickB(null);
              }}
            />
          )}
          <div className="decode-actions">
            <button className="settings-save-btn" type="submit" disabled={busy}>
              {busy ? "Looking up…" : "Look up"}
            </button>
            {mode === "prefix" && (
              <button
                className="sign-out-btn"
                type="button"
                disabled={busy}
                onClick={() => void example()}
              >
                Example
              </button>
            )}
            {(a || b || result) && (
              <button
                className="sign-out-btn"
                type="button"
                onClick={() => {
                  setA("");
                  setB("");
                  setPickA(null);
                  setPickB(null);
                  setResult(null);
                  setError(null);
                  setPending(false);
                }}
              >
                Clear
              </button>
            )}
          </div>
        </form>

        {error && (
          <div className="decode-error" role="alert">
            {error}
          </div>
        )}
        {pending && <div className="node-detail-note">{PENDING_TEXT}</div>}

        {result && result.mode === "prefix" && (
          <>
            <div className="k decode-heading">Each hop</div>
            <HopRows r={result} />
          </>
        )}

        {result && needsChoice(result) && result.source && result.destination && (
          <>
            <EndChoice label="Source" end={result.source} onPick={(n) => pick("a", n)} />
            <EndChoice label="Destination" end={result.destination} onPick={(n) => pick("b", n)} />
          </>
        )}

        {result && !needsChoice(result) && (
          <>
            <div className="k decode-heading">{result.mode === "pair" ? "How packets got there" : "Heard routes"}</div>
            {result.mode === "pair" && result.source?.node && result.destination?.node && (
              <p className="node-detail-note">
                From <NodeLink node={result.source.node}>{nodeLabel(result.source.node)}</NodeLink> to{" "}
                <NodeLink node={result.destination.node}>{nodeLabel(result.destination.node)}</NodeLink>
              </p>
            )}
            {pairNote.map((t) => (
              <p key={t} className="node-detail-note">{t}</p>
            ))}
            <p className="node-detail-note pi-summary">{summaryText(result)}. Index brought up to date {seenText(result.index_synced_at)}.</p>
            {truncatedText(result) && <p className="node-detail-note">{truncatedText(result)}</p>}
            {groups.length === 0 && <div className="empty-state">{NO_ROUTES_TEXT}</div>}
            {resolved.length > 0 && <GroupList groups={resolved} theme={effectiveTheme} />}
            {possible.length > 0 && (
              <GroupList
                groups={possible}
                theme={effectiveTheme}
                title="Could also be these (a hop here is unclear and may be one of the two nodes)"
              />
            )}
          </>
        )}
      </SectionCard>
    </div>
  );
}
